import React, { useState, useEffect, useMemo } from 'react';
import './SlackStyle.css';
import TextEditor from '../TextEditor/APITextEditor';
import useSlackStore, { getSlackStore } from './useSlackStore';
import { getCurrentTime } from '../../utils/getCurrentTime';
import { send_component_save_request, send_request } from '../../utils/Request';
import { injectMetadataFields } from '../../utils/buildComponentSavePayload';
import useProjectStore from '../ProjectCanvas/useProjectStore';
import {
  Grid,
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  Divider,
  styled,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { IndividualComponentLoader } from '../../utils/ComponentLoadingSkeleton';
import CustomPaper from '../common/CustomPaper';

const StyledContainer = styled(Box)(({ theme }) => ({
  padding: '24px',
  borderRadius: '12px',
  backgroundColor: '#fff',
  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)',
}));

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  borderRadius: '8px',
  marginBottom: '12px',
  boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.05)',
  '&:before': {
    display: 'none',
  },
  '&.Mui-expanded': {
    margin: '0 0 12px 0',
  },
}));

const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  padding: '0 16px',
  backgroundColor: '#fafafa',
  borderRadius: '8px',
  '&.Mui-expanded': {
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
  },
}));

const StyledAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
  padding: '16px',
  backgroundColor: '#fff',
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '16px',
  color: 'var(--color-text-primary)',
  marginBottom: '8px',
}));

const StepNumber = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '28px',
  height: '28px',
  borderRadius: '8px',
  backgroundColor: '#000',
  color: '#fff',
  fontWeight: 600,
  marginRight: '12px',
  flexShrink: 0,
}));

const EditorWrapper = styled(Box)(({ theme }) => ({
  borderRadius: '8px',
  padding: '0',
  backgroundColor: '#fff',
  marginTop: '16px',
}));

const SlackTemplate = (props) => {
  const [focus, _setFocus] = useState(null);
  const setFocus = (val) => {
    _setFocus(val);
    focusRef.current = val;
  };
  const focusRef = React.useRef(null);
  const [placeholders, setPlaceholders] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [status, setStatus] = useState(null);

  const store = useMemo(() => getSlackStore(props.component_id), [props.component_id]);

  const data = store(s => s.data);
  const body = data.body;
  const endpoint_url = data.endpoint_url;

  useEffect(() => {
    if (props.component_id !== undefined) {
      // If store was prefilled from canvas load, skip the REST fetch
      if (store.getState().loaded) {
        props.onComponentNameChanged(store.getState().name);
        props.onLastModifiedChanged(store.getState().lastModified);
        setLoaded(true);
        props.setChildTemplateLoaded(true);
        return;
      }

      send_request(
        `project-service/project/component/query/${props.component_id}/at-version/${props.version}`,
        '',
        {}
      )
        .then(response => {
          if (response && response.data) {
            const { components } = response.data;
            if (components) {
              store.getState().setSlackTemplate(components[0]);
              store.setState({ loaded: true });
              props.onComponentNameChanged(store.getState().name);
              props.onComponentDescriptionChanged(components[0].description);
              props.onLastModifiedChanged(store.getState().lastModified);
            }
          }

          props.setChildTemplateLoaded(true);
        })
        .catch(err => {
          throw Error(err.message);
        })
        .finally(() => {
          setLoaded(true);
        });
    }
  }, []);

  useEffect(() => {
    const unsub = useProjectStore.subscribe(
      s => s.selectedPlaceholder,
      (placeholder) => {
        const activeId = useProjectStore.getState().activeComponentId;
        if (activeId !== null && activeId !== props.component_id) return;
        if (!placeholder || !placeholder['selected'] || typeof placeholder['selected'] !== 'object') return;
        const { style, key } = placeholder['selected'];
        if (!style) return;
        insertPlaceholder(style.default, key, style, focusRef.current);
      }
    );
    return unsub;
  }, []);

  const insertPlaceholder = (label, text, style, currentFocus) => {
    if (currentFocus === 'editor') {
      setPlaceholders(prev => [...prev, { label, text, style, focus: currentFocus }]);
    }
  };

  const stripHtml = html => {
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  const getStepContent = () => {
    return (
      <CustomPaper>
        <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
          Slack Integration Setup
        </Typography>

        <Box sx={{ mb: 4 }}>
          <StyledAccordion defaultExpanded>
            <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <StepNumber>1</StepNumber>
                <Typography fontWeight={500}>Create a Slack App</Typography>
              </Box>
            </StyledAccordionSummary>
            <StyledAccordionDetails>
              <Typography variant="body2" component="div">
                <ul style={{ margin: '0', paddingLeft: '20px' }}>
                  <li>
                    Go to{' '}
                    <a
                      href="https://api.slack.com/apps"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: 'var(--color-text-primary)', fontWeight: 600 }}
                    >
                      api.slack.com/apps
                    </a>
                  </li>
                  <li>Click on "Create New App"</li>
                  <li>Choose "From scratch" option</li>
                  <li>Enter a name for your app</li>
                  <li>Select your workspace from the dropdown</li>
                </ul>
              </Typography>
            </StyledAccordionDetails>
          </StyledAccordion>

          <StyledAccordion>
            <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <StepNumber>2</StepNumber>
                <Typography fontWeight={500}>Enable Incoming Webhooks</Typography>
              </Box>
            </StyledAccordionSummary>
            <StyledAccordionDetails>
              <Typography variant="body2" component="div">
                <ul style={{ margin: '0', paddingLeft: '20px' }}>
                  <li>From your app's settings page, find "Features" in the left sidebar</li>
                  <li>Click on "Incoming Webhooks"</li>
                  <li>Toggle the switch to the "On" position</li>
                  <li>This enables incoming webhooks for your app</li>
                </ul>
              </Typography>
            </StyledAccordionDetails>
          </StyledAccordion>

          <StyledAccordion>
            <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <StepNumber>3</StepNumber>
                <Typography fontWeight={500}>Create a Webhook URL</Typography>
              </Box>
            </StyledAccordionSummary>
            <StyledAccordionDetails>
              <Typography variant="body2" component="div">
                <ul style={{ margin: '0', paddingLeft: '20px' }}>
                  <li>Scroll down to the "Webhook URLs for Your Workspace" section</li>
                  <li>Click on "Add New Webhook to Workspace"</li>
                  <li>Select a channel where messages will be posted</li>
                  <li>Click "Allow" to authorize the app</li>
                  <li>Slack will generate your Webhook URL</li>
                </ul>
              </Typography>
            </StyledAccordionDetails>
          </StyledAccordion>

          <StyledAccordion>
            <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <StepNumber>4</StepNumber>
                <Typography fontWeight={500}>Copy Your Webhook URL</Typography>
              </Box>
            </StyledAccordionSummary>
            <StyledAccordionDetails>
              <Typography variant="body2" component="div">
                <ul style={{ margin: '0', paddingLeft: '20px' }}>
                  <li>Find the Webhook URL in the "Webhook URLs for Your Workspace" section</li>
                  <li>The URL starts with "https://hooks.slack.com/services/..."</li>
                  <li>Click the "Copy" button next to the URL</li>
                  <li>Paste this URL in the "Webhook URL" field below</li>
                </ul>
              </Typography>
            </StyledAccordionDetails>
          </StyledAccordion>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mt: 4 }}>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              Webhook URL
            </Typography>
            <EditorWrapper>
              <TextEditor
                availablePlaceholders={props.availablePlaceholders}
                html={endpoint_url}
                onChange={html => {
                  const strippedUrl = stripHtml(html);
                  store.getState().setEndPointUrl(strippedUrl);
                }}
                controlStyles={{ display: 'none' }}
                contentStyles={{
                  backgroundColor: 'white',
                  boxShadow: 'none',
                  padding: '10px',
                  minHeight: '36px',
                  border: '1px solid #eaeaea',
                  borderRadius: '8px',
                }}
                placeholder="Paste your Webhook URL from Slack here"
                editorFocus={() => {
                  setFocus('url_editor');
                }}
              />
            </EditorWrapper>
          </Box>

          <Box sx={{ mt: 4 }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              Message Content
            </Typography>
            <EditorWrapper>
              <TextEditor
                availablePlaceholders={props.availablePlaceholders}
                html={body}
                onChange={html => {
                  if (html !== body) {
                    store.getState().setSlackBody(html);
                  }
                }}
                controlStyles={{ display: 'none' }}
                contentStyles={{
                  backgroundColor: 'white',
                  boxShadow: 'none',
                  padding: '10px',
                  minHeight: '200px',
                  border: '1px solid #eaeaea',
                  borderRadius: '8px',
                }}
                placeholder={placeholders}
                editorFocus={() => {
                  setFocus('editor');
                }}
              />
            </EditorWrapper>
            <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#666' }}>
              Type "$" to view and insert available placeholders
            </Typography>
          </Box>
        </Box>
      </CustomPaper>
    );
  };

  if (loaded) {
    return (
      <Grid container>
        <Grid item xs={12}>
          {getStepContent()}
        </Grid>
      </Grid>
    );
  } else {
    return <IndividualComponentLoader />;
  }
};

export default SlackTemplate;
